import React, { useEffect, useRef, useState } from "react";
import { Clock, Volume2 } from "lucide-react";
import { playStadiumRoar, unlockAudio } from "@/lib/stadiumSound";

const KICK_OFF = new Date("2026-09-11T20:45:00+02:00").getTime();

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    over: diff === 0,
  };
}

export default function CountdownHero() {
  const { days, hours, minutes, seconds, over } = useCountdown(KICK_OFF);
  const firedRef = useRef(false);

  useEffect(() => {
    if (over && !firedRef.current) {
      firedRef.current = true;
      try { playStadiumRoar(); } catch {}
    }
  }, [over]);

  const preview = () => { unlockAudio(); playStadiumRoar(); };

  return (
    <div className="inline-flex items-center gap-1.5 md:gap-2 h-10 md:h-11 px-3 md:px-4 rounded-full bg-slate-900/85 backdrop-blur-md border border-white/25 shadow-xl max-w-full whitespace-nowrap" data-testid="countdown-hero">
      <Clock className="w-4 h-4 shrink-0" style={{ color: "#fbbf24" }} />
      <span className="text-[10px] md:text-[11px] uppercase tracking-widest text-white/90 font-bold hidden xs:inline sm:inline">Al fischio d'inizio</span>
      <span className="text-[10px] uppercase tracking-widest text-white/90 font-bold xs:hidden sm:hidden inline">Kickoff</span>
      <div className="flex items-center gap-1 md:gap-1.5">
        {over ? (
          <span className="font-black text-sm" style={{ color: "#ffffff" }}>Si gioca!</span>
        ) : (
          <>
            <TimeUnit value={days} label="g" />
            <span className="text-white/50">·</span>
            <TimeUnit value={hours} label="h" />
            <span className="text-white/50 hidden sm:inline">·</span>
            <TimeUnit value={minutes} label="m" className="hidden sm:inline-flex" />
            <span className="text-white/50 hidden sm:inline">·</span>
            <TimeUnit value={seconds} label="s" className="hidden sm:inline-flex" />
          </>
        )}
      </div>
      <button
        onClick={preview}
        title="Ascolta il boato di stadio"
        className="ml-1 md:ml-1.5 p-1 md:p-1.5 rounded-full hover:bg-white/15 text-white/85 hover:text-white shrink-0"
        data-testid="countdown-preview-sound"
      >
        <Volume2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function TimeUnit({ value, label, className = "" }) {
  return (
    <span className={`inline-flex items-baseline gap-0.5 ${className}`}>
      <span className="font-black text-sm md:text-base tabular-nums" style={{ color: "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>{String(value).padStart(2, "0")}</span>
      <span className="text-[9px] md:text-[10px] font-bold uppercase" style={{ color: "#fcd34d" }}>{label}</span>
    </span>
  );
}
